import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, BarChart3, Pencil, Trash2, Play, Square, Archive, X, FileText,
} from 'lucide-react';
import { useUser } from '../../../contexts/UserContext';
import PermissionDeniedPage from '../../../pages/PermissionDeniedPage';
import {
  listSurveys, deleteSurvey, setSurveyStatus, getSurvey,
} from '../../../services/api/survey.service';
import type { Survey, SurveyStatus } from '../../../types/survey';
import SurveyBuilder from './SurveyBuilder';
import SurveyResults from './SurveyResults';

type View =
  | { name: 'list' }
  | { name: 'create' }
  | { name: 'edit'; survey: Survey }
  | { name: 'results'; surveyId: number };

const statusStyles: Record<SurveyStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  closed: 'bg-amber-100 text-amber-700',
  archived: 'bg-red-100 text-red-700',
};

const StatusBadge: React.FC<{ status: SurveyStatus }> = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-[0.65rem] font-medium ${statusStyles[status]}`}>
    {status}
  </span>
);

const Surveys: React.FC = () => {
  const { userProfile } = useUser();

  const systemRole = (() => {
    try {
      return sessionStorage.getItem('systemRole') || userProfile?.systemRole || null;
    } catch {
      return null;
    }
  })();
  const isSystemAdmin = systemRole === 'system_admin';

  const [view, setView] = useState<View>({ name: 'list' });
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Survey | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const fetchSurveys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSurveys(await listSurveys());
    } catch (err: any) {
      setError(err.message || 'Failed to load surveys');
      toast.error(err.message || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin && view.name === 'list') {
      fetchSurveys();
    }
  }, [isSystemAdmin, view.name, fetchSurveys]);

  if (!isSystemAdmin) {
    return <PermissionDeniedPage />;
  }

  const openEdit = async (survey: Survey) => {
    try {
      setBusyId(survey.id);
      const full = await getSurvey(survey.id);
      // carry response_count from the list so the builder can lock questions if needed
      setView({ name: 'edit', survey: { ...full, response_count: survey.response_count } });
    } catch (err: any) {
      toast.error(err.message || 'Failed to open survey');
    } finally {
      setBusyId(null);
    }
  };

  const changeStatus = async (survey: Survey, status: SurveyStatus) => {
    try {
      setBusyId(survey.id);
      await setSurveyStatus(survey.id, status);
      toast.success(`Survey ${status === 'active' ? 'published' : status}`);
      await fetchSurveys();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setBusyId(deleteTarget.id);
      const hard = (deleteTarget.response_count || 0) === 0;
      await deleteSurvey(deleteTarget.id, hard);
      toast.success(hard ? 'Survey deleted' : 'Survey archived (responses preserved)');
      setDeleteTarget(null);
      await fetchSurveys();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete survey');
    } finally {
      setBusyId(null);
    }
  };

  if (view.name === 'create') {
    return (
      <SurveyBuilder
        onSaved={() => { setView({ name: 'list' }); }}
        onCancel={() => setView({ name: 'list' })}
      />
    );
  }
  if (view.name === 'edit') {
    return (
      <SurveyBuilder
        initial={view.survey}
        onSaved={() => { setView({ name: 'list' }); }}
        onCancel={() => setView({ name: 'list' })}
      />
    );
  }
  if (view.name === 'results') {
    return (
      <SurveyResults surveyId={view.surveyId} onBack={() => setView({ name: 'list' })} />
    );
  }

  // ---- List view ----
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Surveys</h1>
          <p className="text-[0.75rem] text-gray-500">Ask your users questions and read what they want.</p>
        </div>
        <button
          onClick={() => setView({ name: 'create' })}
          className="flex items-center gap-1.5 bg-black text-white text-[0.75rem] font-medium px-3 py-2 rounded-lg hover:bg-gray-800"
        >
          <Plus size={14} /> New Survey
        </button>
      </div>

      {loading ? (
        <div className="text-[0.8rem] text-gray-500 py-10 text-center">Loading…</div>
      ) : error ? (
        <div className="text-[0.8rem] text-red-600 py-10 text-center">{error}</div>
      ) : surveys.length === 0 ? (
        <div className="text-[0.8rem] text-gray-500 py-16 text-center border border-dashed border-gray-200 rounded-lg">
          No surveys yet. Create your first one.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-100 rounded-lg">
          <table className="w-full text-[0.75rem]">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left font-medium px-3 py-2">Title</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-left font-medium px-3 py-2">Audience</th>
                <th className="text-right font-medium px-3 py-2">Responses</th>
                <th className="text-right font-medium px-3 py-2">Questions</th>
                <th className="text-right font-medium px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-gray-900">{s.title}</div>
                    {s.description && (
                      <div className="text-gray-400 line-clamp-1 max-w-md">{s.description}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge status={s.status} /></td>
                  <td className="px-3 py-2.5 text-gray-600">{s.audience}</td>
                  <td className="px-3 py-2.5 text-right text-gray-900 font-medium">{s.response_count ?? 0}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{s.question_count ?? 0}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        title="Results"
                        onClick={() => setView({ name: 'results', surveyId: s.id })}
                        className="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                      >
                        <BarChart3 size={14} />
                      </button>
                      <button
                        title="Edit"
                        disabled={busyId === s.id}
                        onClick={() => openEdit(s)}
                        className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-40"
                      >
                        <Pencil size={14} />
                      </button>
                      {s.status === 'draft' || s.status === 'closed' ? (
                        <button
                          title="Publish"
                          disabled={busyId === s.id}
                          onClick={() => changeStatus(s, 'active')}
                          className="p-1.5 rounded hover:bg-green-100 text-green-700 disabled:opacity-40"
                        >
                          <Play size={14} />
                        </button>
                      ) : null}
                      {s.status === 'active' ? (
                        <button
                          title="Close"
                          disabled={busyId === s.id}
                          onClick={() => changeStatus(s, 'closed')}
                          className="p-1.5 rounded hover:bg-amber-100 text-amber-700 disabled:opacity-40"
                        >
                          <Square size={14} />
                        </button>
                      ) : null}
                      {s.status !== 'archived' ? (
                        <button
                          title="Archive / Delete"
                          disabled={busyId === s.id}
                          onClick={() => setDeleteTarget(s)}
                          className="p-1.5 rounded hover:bg-red-100 text-red-600 disabled:opacity-40"
                        >
                          {(s.response_count || 0) > 0 ? <Archive size={14} /> : <Trash2 size={14} />}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete / archive confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {(deleteTarget.response_count || 0) > 0
                  ? <Archive size={18} className="text-amber-600" />
                  : <Trash2 size={18} className="text-red-600" />}
                <h2 className="text-sm font-semibold text-gray-900">
                  {(deleteTarget.response_count || 0) > 0 ? 'Archive survey?' : 'Delete survey?'}
                </h2>
              </div>
              <button onClick={() => setDeleteTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
            <p className="text-[0.75rem] text-gray-600 mb-4">
              {(deleteTarget.response_count || 0) > 0 ? (
                <>This survey has <strong>{deleteTarget.response_count}</strong> response(s). It will be
                  archived and hidden, but the collected responses are preserved.</>
              ) : (
                <>“{deleteTarget.title}” has no responses and will be permanently deleted.</>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="text-[0.75rem] px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={busyId === deleteTarget.id}
                className="text-[0.75rem] px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
              >
                <FileText size={13} />
                {(deleteTarget.response_count || 0) > 0 ? 'Archive' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Surveys;
